import { createEndpoint } from "../createEndpoint";
import { HttpClient } from "../httpClient";
import {
  ValidateInviteResponseSchema,
  AcceptInviteResponseSchema,
  type AcceptInviteRequest,
} from "../schemas/invites";

/**
 * Creates the public invites API endpoints.
 *
 * Public Endpoints:
 * - validate: GET /api/v2/auth/invites/validate/:token
 * - accept: POST /api/v2/auth/invites/accept
 */
export function createPublicInvitesApi(http: HttpClient, baseUrl: string) {
  const invitesBaseUrl = `${baseUrl}/auth/invites`;

  return {
    /**
     * Validate an invitation token and retrieve invitation details.
     *
     * GET /api/v2/auth/invites/validate/:token
     *
     * @param params.token - The invitation token from the email link
     * @returns Invitation details (email, first_name, last_name, expires_at)
     *
     * @throws 404 - Invalid invitation token
     * @throws 400 - Token expired or already accepted
     * @throws 409 - User with this email already exists
     *
     * @example
     * ```ts
     * const invite = await api.public.invites.validate({
     *   params: { token: "abc123def456..." }
     * });
     * console.log(invite.email); // "invited@example.com"
     * ```
     */
    validate: createEndpoint<
      typeof ValidateInviteResponseSchema._output,
      unknown,
      Record<string, never>,
      { token: string }
    >(http, "GET", "/validate/:token", {
      base: invitesBaseUrl,
      responseSchema: ValidateInviteResponseSchema,
      params: ["token"],
    }),

    /**
     * Accept an invitation by creating a user account with the provided password.
     *
     * POST /api/v2/auth/invites/accept
     *
     * @param body.token - The invitation token
     * @param body.password - Password (min 8 characters)
     * @param body.password_confirmation - Must match password
     * @returns User info, auth token, client and organization details
     *
     * @throws 404 - Invalid invitation token
     * @throws 400 - Token expired or already accepted
     * @throws 409 - User with this email already exists
     * @throws 422 - Validation errors (password too short, passwords don't match)
     *
     * @example
     * ```ts
     * const result = await api.public.invites.accept({
     *   body: {
     *     token: "abc123def456...",
     *     password: "SecurePassword123!",
     *     password_confirmation: "SecurePassword123!"
     *   }
     * });
     * console.log(result.token); // Auth token for immediate login
     * console.log(result.user.email); // User's email
     * ```
     */
    accept: createEndpoint<
      typeof AcceptInviteResponseSchema._output,
      AcceptInviteRequest
    >(http, "POST", "/accept", {
      base: invitesBaseUrl,
      responseSchema: AcceptInviteResponseSchema,
    }),
  };
}
